import { parse, parseISO } from "date-fns";
import { z } from "zod";
export var SortOrder;
(function (SortOrder) {
    SortOrder["Ascending"] = "+";
    SortOrder["Descending"] = "-";
})(SortOrder || (SortOrder = {}));
export const suffixes = ["II", "III", "IV", "Jr", "Sr"];
export const Suffix = z.enum(suffixes);
export const prefixes = [
    "Dr",
    "Mdm",
    "Mr",
    "Ms",
    "Mrs",
    "Miss",
    "Sir",
    "Prof",
];
export const Prefix = z.enum(prefixes);
export const FartherAccountId = z.custom((val) => typeof val === "string");
export const ExternalAccountId = z.custom((val) => typeof val === "string");
// 2022-01-01
export const LocalDate = z
    .custom((val) => (typeof val === "string" ? /^\d{4}-\d{2}-\d{2}$/.test(val) : false))
    .transform((s) => parse(s, "yyyy-MM-dd", new Date()));
export const DateSchema = z
    .string()
    .datetime()
    .transform((s) => parseISO(s));
export const AltAssetId = z.custom((val) => typeof val === "string");
export const AdvisorId = z.custom((val) => typeof val === "string");
export const ClientId = z.custom((val) => typeof val === "string");
export const PhysicalAccountId = z.custom((val) => typeof val === "string");
export const InstitutionId = z.custom((val) => typeof val === "string");
export const TransferId = z.custom((val) => typeof val === "string");
export const FamilyId = z.custom((val) => typeof val === "string");
export const Ticker = z.custom((val) => typeof val === "string");
export const Cusip = z.custom((val) => typeof val === "string");
export const CustodianAccountId = z.custom((val) => typeof val === "string");
export const PerformanceGroupId = z.custom((val) => typeof val === "string");
export const TaskId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const TaskCommentId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const AttachmentId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const BeneficiaryId = z.custom((val) => typeof val === "string");
export const AgreementId = z.custom((val) => typeof val === "string");
export const SignatureRequestId = z.custom((val) => typeof val === "string");
export const PortfolioId = z
    .string()
    .pipe(z.custom((val) => typeof val === "string"));
export const FundId = z.custom((val) => typeof val === "string");
export const SecurityId = z.custom((val) => typeof val === "string");
export const EntityId = z.custom((val) => typeof val === "string");
export const PlanId = z.custom((val) => typeof val === "string");
export const FeedId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
// We have 2 types of custodians
// Full - Custodians we try to completely integrate with in a rich way
// Limited - Limited types of accounts and our integration level with them
// Full custodians
export const fullCustodianOptions = [
    "Apex",
    "Fidelity",
    "Pershing",
    "Schwab",
];
export const FullCustodian = z.enum(fullCustodianOptions);
export const isFullCustodian = (c) => {
    return fullCustodianOptions.some((fullC) => fullC === c);
};
// Limited custodians
export const limitedCustodianOptions = ["DPL", "Pontera"];
export const LimitedCustodian = z.enum(limitedCustodianOptions);
export const isLimitedCustodian = (c) => {
    return limitedCustodianOptions.some((limitedC) => limitedC === c);
};
export const custodianOptions = [
    ...limitedCustodianOptions,
    ...fullCustodianOptions,
];
export const Custodian = z.enum(custodianOptions);
const retirementDataIRATypes = [
    "Sep IRA",
    "Rollover IRA",
    "Inherited IRA",
    "Inherited Roth IRA",
];
export const RetirementDataIRA = z.enum(retirementDataIRATypes);
const noRetirementDataIRATypes = ["IRA", "Roth IRA"];
export const NoRetirementDataIRA = z.enum(noRetirementDataIRATypes);
export const IRAAccountTypes = [
    ...retirementDataIRATypes,
    ...noRetirementDataIRATypes,
];
export const IRAAccountType = z.enum(IRAAccountTypes);
export const jointAccountTypes = [
    "Joint WROS",
    "Joint TIC",
    "Joint CP",
    "Joint TE",
];
export const JointAccountType = z.enum(jointAccountTypes);
export const accountTypes = [
    ...IRAAccountTypes,
    ...jointAccountTypes,
    "Individual",
    "Trust",
    "Asset Lending",
    "Other",
];
export const AccountType = z.enum(accountTypes);
export const taxTypeOptions = ["SSN", "EIN", "ITIN", "Applied For"];
export const TaxType = z.enum(taxTypeOptions);
export const USAStates = [
    "AK",
    "AL",
    "AR",
    "AZ",
    "CA",
    "CO",
    "CT",
    "DC",
    "DE",
    "FL",
    "GA",
    "GU",
    "HI",
    "IA",
    "ID",
    "IL",
    "IN",
    "KS",
    "KY",
    "LA",
    "MA",
    "MD",
    "ME",
    "MI",
    "MN",
    "MO",
    "MS",
    "MT",
    "NC",
    "ND",
    "NE",
    "NH",
    "NJ",
    "NM",
    "NV",
    "NY",
    "OH",
    "OK",
    "OR",
    "PA",
    "PR",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VA",
    "VI",
    "VT",
    "WA",
    "WI",
    "WV",
    "WY",
    "AA", //Armed Forces America
];
export const USAState = z.enum(USAStates);
export const BeneficiaryTypes = ["Primary", "Contingent"];
export const AgreementSignatureTypes = [
    "None",
    "Offline",
    "eSignature",
    "Click to sign",
];
export const AgreementSignatureStatusOptions = [
    "Planning",
    "Pending",
    "Signed",
    "Declined",
    "Rejected",
    "Timed Out",
    "Expired",
    "Withdrawn",
    "Processing",
    "Draft",
    "Uploaded",
];
export const AgreementSignatureStatus = z.enum(AgreementSignatureStatusOptions);
export const FundingAccountStatusOptions = [
    "Active",
    "Inactive",
    "Planning",
    "Pending",
];
export const PershingStandingInstructionFundingStatusOptions = [
    "Requested",
    "Initiated",
    "Submitted",
    "Active",
    "Canceled",
    "Expired",
    "Pending",
    "Rejected",
    "Suspended",
    "Withdrawn",
];
export const FundingMovementTypeOptions = [
    "ACH",
    "Bank Transfer",
    "Check - First Party",
    "Check - Third Party",
    "Journal",
    "Retirement Tax Witholding", //Witholding is a known typo.
    "Wire - Fed Funds",
    "Wire - Non USD",
];
export const FundingAccountTypeOptions = ["Checking", "Savings"];
export const FundingAccountType = z.enum(FundingAccountTypeOptions);
export const OperationalState = z.enum([
    "Pending",
    "Planning",
    "Open",
    "Error",
    "Closed",
]);
const PortfolioTaxTypes = ["any", "taxable", "tax_exempt"];
export const PortfolioTaxType = z.enum(PortfolioTaxTypes);
export const TradingGroupId = z.custom((val) => typeof val === "string");
export const TriggerRunId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const partners = ["Farther", "Cogent", "WealthPlan"];
export const Organization = z.enum(partners);
export const PartnerInstitutionId = z.custom((val) => typeof val === "number");
export const PortfolioPartnerId = z.custom((val) => typeof val === "number");
export const SleeveId = z
    .string()
    .pipe(z.custom((val) => typeof val === "string"));
export const LiquidationId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const OpportunityId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const TaxBudgetId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const ProposalId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const EquivalentSecurityListId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const IdempotencyKey = z.string().brand("IdempotencyKey");
export const MarketplaceOfferingId = z.custom((val) => typeof val === "string" && z.string().uuid().safeParse(val).success);
export const AdvisorGroupId = z.string().brand("AdvisorGroupId");
export const ServiceTeamId = z.string().uuid().brand("ServiceTeamId");
export const BasePermission = z.enum([
    "Private",
    "Public-Organization",
    "Public-Platform",
]);
export const PaginationMetadata = z.object({
    currentPage: z.number(),
    pageSize: z.number(),
    pageCount: z.number(),
    totalCount: z.number(),
});
export const PortfolioFamilyId = z
    .string()
    .startsWith("IPF_")
    .regex(/^IPF_[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/, {
    message: "PortfolioFamilyId must be in the format IPF_XXXX-XXXX-XXXX",
})
    .brand("PortfolioFamilyId");
export const PortfolioPlanId = z
    .string()
    .startsWith("AIP_")
    .refine((value) => value === value.toUpperCase(), {
    message: "PortfolioPlanId must be uppercase",
})
    .refine((value) => /^AIP_[A-Z][A-Z][A-Z]_[A-Z0-9_:.-]+$/i.test(value), (val) => ({
    message: `PortfolioPlanId must be in the format AIP_XXX_XXXXX , got ${val}`,
}))
    .brand("PortfolioPlanId");
const BasePortfolioFamily = z.object({
    portfolioFamilyId: PortfolioFamilyId,
    portfolioFamilyVersion: z.number(),
    name: z.string(),
    organization: Organization,
    createdAt: DateSchema,
    updatedAt: DateSchema,
});
export const PortfolioFamilyFamilyChild = z.object({
    type: z.literal("Family"),
    portfolioFamily: BasePortfolioFamily.extend({
        isARootFamily: z.literal(false), // Can't be a root family by definition
        children: z.null(),
    }),
});
export const PortfolioFamilyPlanChild = z.object({
    type: z.literal("Portfolio-Plan"),
    portfolioPlan: z.object({
        portfolioPlanId: PortfolioPlanId,
        name: z.string(),
    }),
});
export const PortfolioFamilyChild = z.union([
    PortfolioFamilyFamilyChild,
    PortfolioFamilyPlanChild,
]);
export const PortfolioFamily = BasePortfolioFamily.extend({
    isARootFamily: z.boolean(),
    children: PortfolioFamilyChild.array().describe("Not provided for leaf nodes"),
});
export const PortfolioPlanAssignmentId = z
    .string()
    .brand("PortfolioPlanAssignmentId");
export const VirtualAccountId = FartherAccountId;
export const TransformerId = z.string().brand("TransformerId");
export const resources = [
    "Advisor",
    "AdvisorGroup",
    "Client",
    "EquivalentSecurityList",
    "ExternalAccount",
    "FartherAccount",
    "FartherSecurity",
    "FartherToken",
    "GoalBasedFinancialPlan",
    "GoalBasedFinancialPlanAccount",
    "GoalBasedFinancialPlanIncome",
    "GoalBasedFinancialPlanInsurance",
    "GoalBasedFinancialPlanExpense",
    "GoalBasedFinancialPlanGoal",
    "GoalBasedFinancialPlanSavings",
    "ManualAccount",
    "Organization",
    "PortfolioPlan",
    "PortfolioStoragePortfolio",
    "PortfolioStorageRepository",
    "ServiceTeam",
    "SharingInvitation",
    "StrategicAllocation",
    "TwoFactorSetting",
    "ImplementationPlanTransformer",
    "PortfolioFamily",
    "PortfolioPlanAssignment",
];
export const PrePortfolioPackId = z.string().brand("PrePortfolioPackId");
//# sourceMappingURL=Types.js.map